// Talking to an agent from the office (the owner-agents prototype, "Ask the analyst" and the support
// chat): your questions, newest last, each with the agent's answer and the tool calls behind it.
// A question is queued and the worker answers it, so the screen checks back every couple of
// seconds until the answer is in. A stopped agent, or one without a model, says so instead.

import { useEffect, useState } from 'react';
import { Lock } from '../shared/ApprovalsInbox.tsx';
import { Empty, useAct, useQuery } from '../books/ui.tsx';
import { Loading } from '../shared/Loading.tsx';
import { actionWords, clock } from './model.ts';
import type { AgentsProps } from './props.ts';
import type { ChatTurn } from './types.ts';

const POLL_MS = 1500;
const waiting = (t: ChatTurn) => t.status === 'queued' || t.status === 'running';

function Turn({ t, timeZone }: { t: ChatTurn; timeZone?: string }) {
  return (
    <li className="turn">
      <div className="msg q">
        <span className="small muted">You · {clock(t.askedAt, timeZone)}</span>
        <p>{t.question}</p>
      </div>
      <div className={`msg a${t.status === 'skipped' || t.status === 'failed' ? ' off' : ''}`}>
        {waiting(t) ? (
          <p className="muted" role="status">
            Working on it…
          </p>
        ) : t.answer ? (
          <p>{t.answer}</p>
        ) : (
          <p className="lockwhy">
            <Lock /> {t.stopReason ?? 'No answer.'}
          </p>
        )}
        {t.tools.length > 0 && (
          <details className="tools">
            <summary>
              {t.tools.length} tool call{t.tools.length === 1 ? '' : 's'} behind this answer
            </summary>
            <ul>
              {t.tools.map((c, i) => (
                <li key={i} className="small">
                  <span title={c.action ?? undefined}>{actionWords(c.action, c.actionTitle)}</span>{' '}
                  {c.outcome.replace(/_/g, ' ')}
                  <code className="cmd">{JSON.stringify(c.input)}</code>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </li>
  );
}

export function Chat({
  props,
  agentKey,
  title,
  intro,
  examples,
}: {
  props: AgentsProps;
  agentKey: 'analyst' | 'support';
  title: string;
  intro: string;
  examples: readonly string[];
}) {
  const chat = useQuery<{ turns: ChatTurn[] }>('agents.chat', { agentKey });
  const [question, setQuestion] = useState('');
  const act = useAct(props.toast, () => chat.reload());
  const pending = chat.data?.turns.some(waiting) ?? false;
  // Check back while an answer is on its way.
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(() => void chat.reload(), POLL_MS);
    return () => clearInterval(t);
  }, [pending, chat.reload]);
  const ask = async (q: string) => {
    const r = await act.run(agentKey === 'analyst' ? 'agents.askAnalyst' : 'agents.askSupport', {
      question: q,
    });
    if (r) setQuestion('');
  };
  return (
    <div className="books-page">
      <div className="page-head">
        <h2 className="page-title">{title}</h2>
        <p className="muted">{intro}</p>
      </div>
      <section className="sheet panel chat" aria-label={title}>
        {!chat.data ? (
          <Loading error={chat.error} what="the conversation" retry={() => void chat.reload()} />
        ) : chat.data.turns.length === 0 ? (
          <Empty>Nothing asked yet. Try one of the questions below.</Empty>
        ) : (
          <ol className="turns" aria-live="polite">
            {chat.data.turns.map((t) => (
              <Turn key={t.requestId} t={t} {...(props.timeZone ? { timeZone: props.timeZone } : {})} />
            ))}
          </ol>
        )}
        <div className="chips examples">
          {examples.map((e) => (
            <button key={e} type="button" className="qchip" disabled={act.busy} onClick={() => void ask(e)}>
              {e}
            </button>
          ))}
        </div>
        <form
          className="ask"
          onSubmit={(e) => {
            e.preventDefault();
            if (question.trim()) void ask(question.trim());
          }}
        >
          <label className="sr" htmlFor={`ask-${agentKey}`}>
            Your question
          </label>
          <textarea
            id={`ask-${agentKey}`}
            className="field area"
            rows={2}
            maxLength={2000}
            value={question}
            placeholder="Ask a question"
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (question.trim()) void ask(question.trim());
              }
            }}
          />
          <button className="btn primary" type="submit" disabled={act.busy || !question.trim()}>
            Ask
          </button>
        </form>
      </section>
    </div>
  );
}
